/**
 * Plan-aware recommended files — turns an {@link UploadPlan} into the concrete
 * files the customer should provide, each with the catalog fields we recommend
 * for it. Pure + isomorphic: the guide's summary derives its on-screen sections,
 * template downloads, and the printable plan from this one helper, so the file
 * shape never drifts from the plan vocabulary or the field catalog.
 *
 * The recommendation is tailored to ALL THREE guide answers — not the kitchen sink
 * of every snapshot field:
 *   - originationData → whether origination fields sit on the snapshot, ride a
 *     separate origination file, or are dropped (estimated);
 *   - chargeOff       → whether credit-loss fields are recommended on the snapshot
 *     (in_snapshots) or omitted because they arrive separately;
 *   - prepayment      → which partial-prepayment fields match the detection method
 *     the customer said their data supports.
 *
 * The field SETS below are anchored to the modeling field-sets (`modeling-readiness`)
 * and the catalog: each set is the matching option's fields for that output, so the
 * template asks for exactly what the chosen approach needs — including the reported
 * scheduled principal BOTH partial-prepayment methods require for the speed
 * denominator (RB-4/RB-8; the product never reconstructs a schedule).
 */

import { fieldsForFileType, UNIVERSAL_BASE_FIELD_KEYS } from './catalog';
import type { StandardFieldKey } from './catalog';
import type { FileType } from './types';
import { type PrepaymentOption, type UploadPlan } from './upload-plan';

/** One file the customer should provide, with its recommended catalog fields. */
export interface PlanFile {
  readonly fileType: FileType; // 'snapshot' | 'origination' | 'transaction'
  readonly fieldKeys: readonly StandardFieldKey[]; // recommended fields (catalog keys), plan-aware, deduped
}

/**
 * The origination-DATA field keys (catalog `scope: 'both'`): the origination date
 * and the original loan amount. Recommended on the snapshot only when the plan keeps
 * origination in the snapshots (otherwise they ride the origination file, or are
 * estimated).
 */
const ORIGINATION_DATA_FIELD_KEYS: readonly StandardFieldKey[] = [
  'origination_date',
  'original_loan_amount',
];

/**
 * Credit-loss fields recommended on the snapshot when the customer says charge-offs
 * live there — net loss, or gross charge-off + recoveries (the two amount-based
 * methods of the `credit_loss` output). Omitted entirely when charge-offs arrive in
 * a separate file.
 */
const CHARGE_OFF_SNAPSHOT_FIELD_KEYS: readonly StandardFieldKey[] = [
  'net_charge_off_amount',
  'charge_off_amount',
  'recovery_amount',
];

/**
 * Fields recommended on a separate TRANSACTIONS file (when the customer
 * says charge-offs arrive separately). A join key so the transactions can be tied
 * back to the snapshots (Loan ID, with Tax ID as the fallback key), plus the
 * charge-off amount fields the `credit_loss` output reads.
 */
const TRANSACTION_FILE_FIELD_KEYS: readonly StandardFieldKey[] = [
  'loan_id',
  'tax_id',
  'net_charge_off_amount',
  'charge_off_amount',
  'recovery_amount',
];

/**
 * The simplest full-payoff signal — always recommended (full payoff is an always-
 * modeled output, and a payoff flag + amount is the lowest-effort way to provide it).
 */
const FULL_PAYOFF_FIELD_KEYS: readonly StandardFieldKey[] = [
  'payoff_flag',
  'payoff_amount',
];

/**
 * Partial-prepayment fields per the customer's stated detection method — the fields
 * of the matching `partial_prepayment` option. BOTH methods include reported
 * scheduled principal (the canonical `scheduled_principal_due`): the speed's
 * denominator always needs it (RB-4), so the template never recommends a field set
 * the derive would then exclude from the speed measure (RB-8).
 */
const PREPAYMENT_FIELD_KEYS: Readonly<Record<PrepaymentOption, readonly StandardFieldKey[]>> = {
  amount_in_snapshots: ['partial_prepayment_amount', 'scheduled_principal_due'],
  expected_vs_actual: ['actual_principal_paid', 'scheduled_principal_due'],
};

/** Recommended catalog keys for a file type — modeling-relevant fields only. */
function recommend(fileType: FileType): readonly StandardFieldKey[] {
  return fieldsForFileType(fileType)
    .filter((f) => !f.recognizedNonModeling)
    .map((f) => f.key);
}

/** Dedupe a key list, preserving first-seen order. */
function dedupe(keys: readonly StandardFieldKey[]): readonly StandardFieldKey[] {
  return [...new Set(keys)];
}

/**
 * The files the customer should provide for a given plan, in upload order: the
 * snapshot file always (its fields tailored to all three answers), then an
 * origination file only when the plan carries origination as its own file.
 */
export function recommendedFilesForPlan(plan: UploadPlan): readonly PlanFile[] {
  const files: PlanFile[] = [];

  // Snapshot file — assembled from the universal base + each answer's recommended
  // fields. Order reads top-to-bottom: identity/base, origination (if in snapshots),
  // credit-loss (if in snapshots), full payoff, then the prepayment method's fields.
  const snapshotKeys: StandardFieldKey[] = [
    ...UNIVERSAL_BASE_FIELD_KEYS,
    ...(plan.originationData === 'in_snapshots' ? ORIGINATION_DATA_FIELD_KEYS : []),
    ...(plan.chargeOff === 'in_snapshots' ? CHARGE_OFF_SNAPSHOT_FIELD_KEYS : []),
    ...FULL_PAYOFF_FIELD_KEYS,
    ...PREPAYMENT_FIELD_KEYS[plan.prepayment],
  ];
  files.push({ fileType: 'snapshot', fieldKeys: dedupe(snapshotKeys) });

  // Origination file — only when the plan carries origination as its own file. Its
  // fields are the catalog's origination-scoped modeling fields.
  if (plan.originationData === 'separate_file') {
    files.push({ fileType: 'origination', fieldKeys: dedupe(recommend('origination')) });
  }

  // Transaction file — only when charge-offs arrive separately. A join key plus the
  // charge-off amount fields (credit-loss is modeled after joining to the snapshots).
  if (plan.chargeOff === 'separate_file') {
    files.push({ fileType: 'transaction', fieldKeys: dedupe(TRANSACTION_FILE_FIELD_KEYS) });
  }

  return files;
}
